import React, { useState, useEffect } from 'react';
import { Search, X, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { getLevelColor } from './utils';
import { useAdminApi } from './useAdminApi';

const DailySchedulerTab = () => {
  const { adminApi } = useAdminApi();
  const today = new Date().toISOString().split('T')[0];

  // Sub-tab state
  const [subTab, setSubTab] = useState('schedule');

  // ── Thought Schedule state ────────────────────────────────────────────────
  const [thoughts, setThoughts] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerLevel, setPickerLevel] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // ── Past Schedule state ───────────────────────────────────────────────────
  const [pastSchedule, setPastSchedule] = useState([]);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastLoaded, setPastLoaded] = useState(false);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchSchedule();
    fetchThoughts();
  }, []);

  // Load past schedule lazily when tab is first activated
  useEffect(() => {
    if (subTab === 'past' && !pastLoaded) {
      fetchPastSchedule();
    }
  }, [subTab]);

  const fetchSchedule = async () => {
    setScheduleLoading(true);
    try {
      const res = await adminApi('get', '/admin/schedule?days_back=0&days_ahead=60');
      setSchedule(res.data);
    } finally {
      setScheduleLoading(false);
    }
  };

  const fetchPastSchedule = async () => {
    setPastLoading(true);
    try {
      // days_back=365, days_ahead=0 gives today-365 through today;
      // we then filter to only days before today
      const res = await adminApi('get', '/admin/schedule?days_back=365&days_ahead=0');
      const pastOnly = res.data
        .filter(e => e.date < today && e.is_scheduled)
        .sort((a, b) => b.date.localeCompare(a.date)); // most recent first
      setPastSchedule(pastOnly);
      setPastLoaded(true);
    } finally {
      setPastLoading(false);
    }
  };

  const fetchThoughts = async () => {
    try {
      const res = await adminApi('get', '/admin/thoughts?limit=10000&sort_by=newest');
      setThoughts(res.data.items || []);
    } catch { /* silent */ }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleScheduleSave = async (thoughtId) => {
    if (scheduleSaving) return;
    setScheduleSaving(true);
    try {
      await adminApi('post', '/admin/schedule', { date: selectedDate, thought_id: thoughtId });
      const dateObj = new Date(selectedDate + 'T00:00:00');
      const formatted = dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
      toast.success(`Scheduled for ${formatted}`);
      setSelectedDate(null);
      setPickerSearch('');
      fetchSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleUnschedule = async (dateStr) => {
    if (!window.confirm('Remove this scheduled thought?')) return;
    try {
      await adminApi('delete', `/admin/schedule/${dateStr}`);
      toast.success('Removed from schedule');
      if (selectedDate === dateStr) setSelectedDate(null);
      fetchSchedule();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const scheduledUpcoming = schedule.filter(d => d.is_scheduled && d.date >= today);
  const filteredPickerThoughts = selectedDate
    ? thoughts.filter(t => {
        if (pickerSearch && !t.content.toLowerCase().includes(pickerSearch.toLowerCase())) return false;
        if (pickerLevel && t.level !== pickerLevel) return false;
        return true;
      })
    : [];

  return (
    <div data-testid="scheduler-tab">
      {/* ── Sub-tab switcher ── */}
      <div className="flex items-center gap-1 mb-8 p-1 rounded-xl bg-charcoal/5 w-fit">
        <button
          onClick={() => setSubTab('schedule')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
            subTab === 'schedule'
              ? 'bg-white shadow-sm text-charcoal'
              : 'text-charcoal/50 hover:text-charcoal/70'
          }`}
          data-testid="subtab-schedule"
        >
          <Calendar size={15} />
          Thought Schedule
        </button>
        <button
          onClick={() => setSubTab('past')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
            subTab === 'past'
              ? 'bg-white shadow-sm text-charcoal'
              : 'text-charcoal/50 hover:text-charcoal/70'
          }`}
          data-testid="subtab-past"
        >
          <Clock size={15} />
          Past Schedule
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SUB-TAB: Thought Schedule
      ══════════════════════════════════════════════════════════════════════ */}
      {subTab === 'schedule' && (
        <div className="flex gap-8 items-start">
          {/* Left: Timeline */}
          <div className="flex-1 min-w-0">
            <div className="mb-6">
              <h2 className="font-serif text-xl text-charcoal">Thought Schedule</h2>
              <p className="text-charcoal/50 text-sm mt-1">
                {scheduledUpcoming.length} of 61 days scheduled · Today through next 60 days
              </p>
            </div>

            {scheduleLoading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin mx-auto" />
              </div>
            ) : (
              <div className="space-y-1.5">
                {schedule.map(entry => {
                  const isToday = entry.date === today;
                  const dateObj = new Date(entry.date + 'T00:00:00');
                  const dayLabel = isToday
                    ? 'Today'
                    : dateObj.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                  const isSelected = selectedDate === entry.date;

                  return (
                    <div
                      key={entry.date}
                      data-testid={`schedule-${entry.date}`}
                      className={`p-4 rounded-xl border flex items-start gap-4 transition-all ${
                        isSelected
                          ? 'border-orange-primary/40 bg-orange-primary/5'
                          : isToday
                          ? 'border-orange-primary/20 bg-orange-primary/[0.03]'
                          : 'border-charcoal/5 bg-white/50 hover:bg-white/70'
                      }`}
                    >
                      {/* Date label */}
                      <div className="w-28 flex-shrink-0 pt-0.5">
                        <span className={`text-sm font-medium ${isToday ? 'text-orange-primary' : 'text-charcoal/70'}`}>
                          {dayLabel}
                        </span>
                        {isToday && (
                          <span className="block text-xs text-orange-primary/60 mt-0.5">Today</span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {entry.is_scheduled ? (
                          <>
                            <p className="leading-relaxed text-sm text-charcoal">{entry.content}</p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${getLevelColor(entry.level)}`}>{entry.level}</span>
                              {(entry.types || []).map(t => (
                                <span key={t} className="px-2 py-0.5 rounded text-xs bg-charcoal/10 text-charcoal/70">{t}</span>
                              ))}
                            </div>
                          </>
                        ) : (
                          <span className="text-charcoal/30 text-sm italic">
                            Not scheduled — app will skip Today's Thought
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex gap-2 items-center">
                        <button
                          onClick={() => {
                            setSelectedDate(isSelected ? null : entry.date);
                            setPickerSearch('');
                            setPickerLevel('');
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            isSelected
                              ? 'bg-orange-primary text-white'
                              : entry.is_scheduled
                              ? 'bg-charcoal/10 text-charcoal/60 hover:bg-charcoal/15'
                              : 'bg-orange-primary/10 text-orange-primary hover:bg-orange-primary/20'
                          }`}
                          data-testid={`schedule-btn-${entry.date}`}
                        >
                          {isSelected ? 'Cancel' : entry.is_scheduled ? 'Change' : '+ Schedule'}
                        </button>
                        {entry.is_scheduled && (
                          <button
                            onClick={() => handleUnschedule(entry.date)}
                            className="px-2 py-1.5 rounded-lg text-xs text-red-400 hover:bg-red-50 transition-colors"
                            data-testid={`unschedule-btn-${entry.date}`}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: Thought Picker */}
          {selectedDate && (
            <div className="w-96 flex-shrink-0" data-testid="thought-picker-panel">
              <div className="sticky top-24 border border-charcoal/10 rounded-2xl overflow-hidden" style={{ backgroundColor: 'rgba(255,252,248,0.9)' }}>
                <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium text-charcoal/40 uppercase tracking-wide">Scheduling for</span>
                    <p className="font-medium text-charcoal text-sm mt-0.5">
                      {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <button onClick={() => setSelectedDate(null)} className="p-1.5 rounded-lg hover:bg-charcoal/5 text-charcoal/40 transition-colors">
                    <X size={16} />
                  </button>
                </div>

                <div className="p-4 border-b border-charcoal/10">
                  <div className="relative mb-3">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
                    <input
                      type="text"
                      placeholder="Search thoughts…"
                      value={pickerSearch}
                      onChange={(e) => setPickerSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 rounded-lg border border-charcoal/10 bg-white/80 focus:outline-none focus:border-orange-primary/50 text-sm"
                      data-testid="picker-search"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {[['', 'All levels'], ['spark', 'Spark'], ['depth', 'Depth'], ['breakthrough', 'Breakthrough']].map(([val, label]) => (
                      <button
                        key={val || 'all'}
                        onClick={() => setPickerLevel(val)}
                        className={`px-3 py-1 rounded-full text-xs transition-colors ${
                          pickerLevel === val
                            ? 'bg-orange-primary/15 text-orange-primary font-medium'
                            : 'bg-charcoal/8 text-charcoal/50 hover:bg-charcoal/12'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="px-4 py-2 text-xs text-charcoal/40 border-b border-charcoal/5">
                  {filteredPickerThoughts.length} thoughts{filteredPickerThoughts.length > 50 ? ' — showing first 50' : ''}
                </div>

                <div className="overflow-y-auto divide-y divide-charcoal/5" style={{ maxHeight: 'calc(100vh - 440px)', minHeight: '180px' }}>
                  {filteredPickerThoughts.length === 0 ? (
                    <div className="p-6 text-center text-charcoal/40 text-sm">No thoughts found</div>
                  ) : (
                    filteredPickerThoughts.slice(0, 50).map(t => (
                      <div key={t.id} className="p-4 hover:bg-orange-primary/[0.03] transition-colors" data-testid={`picker-thought-${t.id}`}>
                        <p className="text-sm text-charcoal leading-relaxed mb-2">{t.content}</p>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex gap-1.5 flex-wrap">
                            <span className={`px-2 py-0.5 rounded text-xs ${getLevelColor(t.level)}`}>{t.level}</span>
                            {t.rating_count > 0 && (
                              <span className="px-2 py-0.5 rounded text-xs bg-orange-primary/10 text-orange-primary">{t.rating_avg}/10</span>
                            )}
                          </div>
                          <button
                            onClick={() => handleScheduleSave(t.id)}
                            disabled={scheduleSaving}
                            className="flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium text-white disabled:opacity-60 transition-opacity"
                            style={{ background: '#ff780a' }}
                            data-testid={`picker-select-${t.id}`}
                          >
                            {scheduleSaving ? '…' : 'Select'}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          SUB-TAB: Past Schedule
      ══════════════════════════════════════════════════════════════════════ */}
      {subTab === 'past' && (
        <div data-testid="past-schedule-tab">
          <div className="mb-6">
            <h2 className="font-serif text-xl text-charcoal">Past Schedule</h2>
            <p className="text-charcoal/50 text-sm mt-1">
              {pastLoaded
                ? `${pastSchedule.length} thought${pastSchedule.length !== 1 ? 's' : ''} scheduled in the past year`
                : 'Loading history…'}
            </p>
          </div>

          {pastLoading ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin mx-auto mb-3" />
              <p className="text-charcoal/40 text-sm">Loading past schedule…</p>
            </div>
          ) : pastSchedule.length === 0 && pastLoaded ? (
            <div className="text-center py-16">
              <Clock size={36} className="text-charcoal/20 mx-auto mb-3" />
              <p className="text-charcoal/50 font-medium">No past thoughts scheduled</p>
              <p className="text-charcoal/30 text-sm mt-1">Thoughts you schedule will appear here after their date passes.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pastSchedule.map(entry => {
                const dateObj = new Date(entry.date + 'T00:00:00');
                const dayLabel = dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                const shortDate = dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });

                return (
                  <div
                    key={entry.date}
                    data-testid={`past-${entry.date}`}
                    className="p-4 rounded-xl border border-charcoal/5 bg-white/40 flex items-start gap-4"
                  >
                    {/* Date */}
                    <div className="w-24 flex-shrink-0 pt-0.5">
                      <span className="text-xs font-medium text-charcoal/50 leading-tight block">{dayLabel.split(',')[0]}</span>
                      <span className="text-sm font-medium text-charcoal/70">{shortDate}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-charcoal/80 leading-relaxed">{entry.content}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${getLevelColor(entry.level)}`}>{entry.level}</span>
                        {(entry.types || []).map(t => (
                          <span key={t} className="px-2 py-0.5 rounded text-xs bg-charcoal/8 text-charcoal/50">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DailySchedulerTab;
